import { useState, useEffect, useMemo } from "react";

interface CountdownTimerOptions {
  targetTime?: string | number;
  countdownSeconds: number;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface CountdownTimerResult {
  timeLeft: TimeLeft;
  isCountdownOver: boolean;
  resetCountdown: () => void;
}

/**
 *
 * @param targetTime target time in date format ex: new Date("2023-05-01T00:00:00Z").getTime()
 * @param countdownSeconds remaining time in seconds
 * @returns an object containing the remaining time, countdown status, and reset function
 */
export function useCountdownTimer({
  targetTime,
  countdownSeconds,
}: CountdownTimerOptions): CountdownTimerResult {
  const [reset, setReset] = useState(false);
  const [isCountdownOver, setIsCountdownOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const targetDate = useMemo(() => {
    if (targetTime) {
      return new Date(targetTime);
    }

    const date = new Date();
    date.setSeconds(date.getSeconds() + countdownSeconds + 1);

    return date;
  }, [countdownSeconds, targetTime, reset]);

  const resetCountdown = () => {
    setReset((prev) => !prev);
    setIsCountdownOver(false);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate.getTime() - now;

      if (difference < 0) {
        clearInterval(interval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsCountdownOver(true);
      } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [countdownSeconds, targetTime, reset]);

  return { timeLeft, isCountdownOver, resetCountdown };
}
